import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Save,
  Upload,
  X,
  Loader2,
  User,
  Car,
  Pencil,
  Trash2,
  Send,
  UserPlus,
  Receipt,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const US_STATES = "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
const PERMIT_TYPES = ["Resident", "Visitor", "Reserved", "Staff", "Guest", "Other"];

const emptyTenant = { full_name: "", phone: "", email: "", unit_number: "", building: "" };
const emptyVehicle = (state = "CA") => ({
  year: "", make: "", model: "", color: "",
  license_plate: "", state, images: [],
  sticker_color: "", permit_type: "",
});

/**
 * Onboard wizard — Tenant → Vehicle → (Save & add another vehicle / Save vehicle / Save & add another tenant).
 *
 * Modes:
 *   • `?tenant=ID`         — tenant exists, jump straight to vehicle step.
 *   • `?edit_tenant=ID`    — full edit view: tenant details + list of attached
 *                            vehicles (each with inline edit / delete / add-vehicle CTA).
 *   • (no params)          — full new-tenant + new-vehicle wizard.
 *
 * Activation: managers never activate QR codes manually. Vehicles are
 * saved and ScanMint flips them to active automatically once the
 * account setup payment + invoice/billing rules allow it (setup-fee
 * webhook for invoice mode, or per-vehicle activation modal for
 * super-admin / demo properties only).
 */
export default function Onboard() {
  const { activeProperty } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const presetTenantId = params.get("tenant");
  const editTenantId = params.get("edit_tenant");
  const initialStep = editTenantId ? "edit-tenant" : presetTenantId ? "vehicle" : "tenant";
  const [step, setStep] = useState(initialStep);
  const [tenant, setTenantForm] = useState(emptyTenant);
  const [tenantId, setTenantId] = useState(editTenantId || presetTenantId || null);
  const [tenantLabel, setTenantLabel] = useState("");
  const [tenantVehicles, setTenantVehicles] = useState([]); // edit-tenant mode
  const [vehicle, setVehicle] = useState(emptyVehicle());
  const [editingVehicleId, setEditingVehicleId] = useState(null); // when editing a vehicle
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Invoice mode is the default. In invoice mode the post-save CTA
  // routes the manager directly to the Onboard Review page where they
  // can issue/pay the invoice that auto-activates QR verification —
  // skipping any per-vehicle activation modal.
  const isInvoiceMode =
    !activeProperty?.is_demo &&
    (activeProperty?.billing_mode || "invoice") === "invoice";

  // Load tenant + (in edit mode) attached vehicles
  useEffect(() => {
    const id = editTenantId || presetTenantId;
    if (!id) return;
    api.get(`/tenants/${id}`).then((r) => {
      setTenantLabel(`${r.data.full_name} · Unit ${r.data.unit_number}`);
      if (editTenantId) {
        setTenantForm({
          full_name: r.data.full_name || "",
          phone: r.data.phone || "",
          email: r.data.email || "",
          unit_number: r.data.unit_number || "",
          building: r.data.building || "",
        });
      }
    }).catch(() => {});
  }, [editTenantId, presetTenantId]);

  const loadTenantVehicles = async () => {
    if (!editTenantId || !activeProperty?.id) return;
    try {
      const { data } = await api.get(
        `/vehicles?property_id=${activeProperty.id}&tenant_id=${editTenantId}`
      );
      setTenantVehicles(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    loadTenantVehicles();
    // eslint-disable-next-line
  }, [editTenantId, activeProperty?.id]);

  const setTenant = (k, v) => setTenantForm((t) => ({ ...t, [k]: v }));
  const setVeh = (k, v) => setVehicle((c) => ({ ...c, [k]: v }));

  const saveTenant = async () => {
    if (!tenant.full_name || !tenant.phone || !tenant.unit_number) {
      toast.error("Full name, phone, and unit are required");
      return;
    }
    setBusy(true);
    try {
      if (editTenantId) {
        // edit-tenant mode — PATCH and stay on the same screen
        await api.patch(`/tenants/${editTenantId}`, tenant);
        setTenantLabel(`${tenant.full_name} · Unit ${tenant.unit_number}`);
        toast.success("Tenant updated");
      } else {
        const { data } = await api.post("/tenants", {
          ...tenant,
          property_id: activeProperty.id,
        });
        setTenantId(data.id);
        setTenantLabel(`${data.full_name} · Unit ${data.unit_number}`);
        setStep("vehicle");
        toast.success(`Tenant added — ${data.full_name}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startEditVehicle = (v) => {
    setEditingVehicleId(v.id);
    setVehicle({
      year: v.year || "",
      make: v.make || "",
      model: v.model || "",
      color: v.color || "",
      license_plate: v.license_plate || "",
      state: v.state || "CA",
      images: v.images || [],
      sticker_color: v.sticker_color || "",
      permit_type: v.permit_type || "",
    });
    setStep("vehicle");
  };

  const removeVehicle = async (vid) => {
    if (!window.confirm("Delete this vehicle? This will deactivate its QR code.")) return;
    try {
      await api.delete(`/vehicles/${vid}`);
      toast.success("Vehicle deleted");
      loadTenantVehicles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFiles = async (files) => {
    const arr = Array.from(files).slice(0, 4 - vehicle.images.length);
    if (arr.length === 0) return;
    // Compress on the client before storing — full-res phone photos are
    // ~3-5 MB each and balloon Mongo + slow down the public scan page.
    const b64s = await Promise.all(arr.map((f) => compressImage(f)));
    const filtered = b64s.filter(Boolean);
    setVehicle((v) => ({ ...v, images: [...v.images, ...filtered].slice(0, 4) }));
  };

  const saveVehicle = async (mode /* "another" | "another-tenant" | "finish" */) => {
    if (!vehicle.license_plate || !vehicle.year || !vehicle.make) {
      toast.error("Year, make, and license plate are required");
      return;
    }
    setBusy(true);
    try {
      let v;
      if (editingVehicleId) {
        // edit existing vehicle (in tenant-edit mode)
        const { data } = await api.patch(`/vehicles/${editingVehicleId}`, vehicle);
        v = data;
        toast.success(`Vehicle updated — ${v.year} ${v.make} ${v.model}`);
      } else {
        const { data } = await api.post("/vehicles", {
          ...vehicle,
          property_id: activeProperty.id,
          tenant_id: tenantId,
        });
        v = data;
        // Single, mode-correct success message. We never tell managers
        // to "activate" — activation is automatic once the account
        // setup payment + billing rules allow it.
        if (activeProperty?.setup_fee_paid_at) {
          toast.success(
            `Vehicle saved — ${v.year} ${v.make} ${v.model}. ` +
              "QR verification will activate based on your billing settings."
          );
        } else {
          toast.success(
            `Vehicle saved — ${v.year} ${v.make} ${v.model}. ` +
              "QR verification will activate automatically once your account setup payment is complete."
          );
        }
      }
      if (mode === "another") {
        setEditingVehicleId(null);
        setVehicle(emptyVehicle(vehicle.state));
      } else if (mode === "another-tenant") {
        // Save current work, then reset everything for the next tenant.
        setEditingVehicleId(null);
        setTenantId(null);
        setTenantForm(emptyTenant);
        setVehicle(emptyVehicle(vehicle.state));
        setStep("tenant");
        toast.message("Ready for the next tenant");
      } else {
        // "finish" — return to context-appropriate page.
        // Invoice mode (the default) routes managers straight to the
        // Onboard Review page so they can issue/pay the invoice that
        // auto-activates QR verification. Auto-pay mode falls back to
        // the Tenants list, where managers manage existing tenants.
        if (editTenantId) {
          setEditingVehicleId(null);
          setVehicle(emptyVehicle());
          setStep("edit-tenant");
          loadTenantVehicles();
        } else if (isInvoiceMode) {
          nav("/dashboard/onboard/review");
        } else {
          nav("/dashboard/tenants");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <Button variant="ghost" onClick={() => nav("/dashboard/tenants")} className="mb-2" data-testid="onboard-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> {editTenantId ? "Back to tenants" : "Cancel"}
      </Button>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="mono-tiny text-[#71717A]">{editTenantId ? "Edit tenant" : "Onboard"}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            {step === "edit-tenant"
              ? "Tenant & vehicles"
              : step === "tenant"
              ? "Add a tenant"
              : editingVehicleId
              ? "Edit vehicle"
              : "Add a vehicle"}
          </h1>
          {step === "vehicle" && tenantLabel && (
            <p className="text-sm text-[#71717A] mt-1">
              For <b className="text-[#09090B]">{tenantLabel}</b>
            </p>
          )}
        </div>
        {!editTenantId && !editingVehicleId && (
          <Button
            onClick={() => nav("/dashboard/onboard/review")}
            variant="outline"
            className="h-11 border-[#0F8F4F] text-[#0F8F4F] hover:bg-[#0F8F4F] hover:text-white"
            data-testid="onboard-review-cta"
          >
            <Send className="w-4 h-4 mr-2" /> Review &amp; start your service
          </Button>
        )}
      </div>

      {/* Step pills — hide in edit-tenant mode (single-page form) */}
      {step !== "edit-tenant" && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <StepBadge num={1} label="Tenant" icon={User} active={step === "tenant"} done={step === "vehicle"} />
          <StepBadge num={2} label="Vehicle" icon={Car} active={step === "vehicle"} done={false} />
        </div>
      )}

      {step === "edit-tenant" && (
        <div className="space-y-8" data-testid="edit-tenant-step">
          {/* === Section 1: Tenant Info === */}
          <section className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-3 pb-4 border-b border-[#E4E4E7]">
              <div className="w-10 h-10 rounded-md bg-[#0F8F4F]/10 flex items-center justify-center">
                <User className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="mono-tiny text-[#71717A]">Section 1</div>
                <div className="font-display text-lg font-bold">Tenant info</div>
              </div>
            </div>
            <FieldRow>
              <Field label="Full name" required>
                <Input value={tenant.full_name} onChange={(e) => setTenant("full_name", e.target.value)} className="h-12" data-testid="edit-tenant-name" />
              </Field>
            </FieldRow>
            <FieldRow cols={2}>
              <Field label="Phone" required>
                <Input value={tenant.phone} onChange={(e) => setTenant("phone", e.target.value)} className="h-12" data-testid="edit-tenant-phone" />
              </Field>
              <Field label="Email (optional)">
                <Input type="email" value={tenant.email} onChange={(e) => setTenant("email", e.target.value)} className="h-12" data-testid="edit-tenant-email" />
              </Field>
            </FieldRow>
            <FieldRow cols={2}>
              <Field label="Unit number" required>
                <Input value={tenant.unit_number} onChange={(e) => setTenant("unit_number", e.target.value)} className="h-12" data-testid="edit-tenant-unit" />
              </Field>
              <Field label="Building (optional)">
                <Input value={tenant.building} onChange={(e) => setTenant("building", e.target.value)} className="h-12" data-testid="edit-tenant-building" />
              </Field>
            </FieldRow>
            <div className="flex justify-end pt-2">
              <Button onClick={saveTenant} disabled={busy} className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]" data-testid="edit-tenant-save">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save tenant changes
              </Button>
            </div>
          </section>

          {/* === Section 2: Vehicles List === */}
          <section className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-4" data-testid="edit-tenant-vehicles">
            <div className="flex items-center justify-between gap-3 flex-wrap pb-4 border-b border-[#E4E4E7]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[#0EA5E9]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="mono-tiny text-[#71717A]">Section 2</div>
                  <div className="font-display text-lg font-bold">
                    Vehicles ({tenantVehicles.length})
                  </div>
                </div>
              </div>
              <Button
                onClick={() => {
                  setEditingVehicleId(null);
                  setVehicle(emptyVehicle());
                  setStep("vehicle");
                }}
                className="h-12 px-5 bg-[#0F8F4F] hover:bg-[#0A6B3B] shadow-sm"
                data-testid="edit-tenant-add-vehicle"
              >
                <Car className="w-4 h-4 mr-2" /> Add vehicle
              </Button>
            </div>
            {tenantVehicles.length === 0 ? (
              <div className="text-center py-8">
                <Car className="w-10 h-10 mx-auto text-[#A1A1AA] mb-3" strokeWidth={1.5} />
                <div className="text-sm text-[#71717A]">
                  No vehicles attached yet.
                </div>
                <div className="text-xs text-[#A1A1AA] mt-1">
                  Click <b>Add vehicle</b> above to register the first one.
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[#E4E4E7]">
                {tenantVehicles.map((v) => (
                  <div key={v.id} className="py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`edit-tenant-vehicle-${v.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {v.year} {v.make} {v.model} <span className="text-[#71717A]">· {v.color}</span>
                      </div>
                      <div className="mono-tiny text-[#71717A] mt-0.5">
                        {v.license_plate} · {v.state}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase border ${
                        v.status === "active"
                          ? "bg-[#0F8F4F]/10 text-[#0F8F4F] border-[#0F8F4F]/30"
                          : v.status === "suspended"
                          ? "bg-[#EF4444]/10 text-[#B91C1C] border-[#EF4444]/30"
                          : "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]"
                      }`}
                    >
                      {v.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => startEditVehicle(v)} data-testid={`edit-tenant-vehicle-edit-${v.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeVehicle(v.id)} className="text-[#EF4444]" data-testid={`edit-tenant-vehicle-delete-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {step === "tenant" && (
        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-4" data-testid="onboard-tenant-step">
          <FieldRow>
            <Field label="Full name" required>
              <Input value={tenant.full_name} onChange={(e) => setTenant("full_name", e.target.value)} className="h-12" data-testid="onboard-tenant-name" />
            </Field>
          </FieldRow>
          <FieldRow cols={2}>
            <Field label="Phone" required>
              <Input value={tenant.phone} onChange={(e) => setTenant("phone", e.target.value)} className="h-12" data-testid="onboard-tenant-phone" />
            </Field>
            <Field label="Email (optional)">
              <Input type="email" value={tenant.email} onChange={(e) => setTenant("email", e.target.value)} className="h-12" data-testid="onboard-tenant-email" />
            </Field>
          </FieldRow>
          <FieldRow cols={2}>
            <Field label="Unit number" required>
              <Input value={tenant.unit_number} onChange={(e) => setTenant("unit_number", e.target.value)} className="h-12" data-testid="onboard-tenant-unit" />
            </Field>
            <Field label="Building (optional)">
              <Input value={tenant.building} onChange={(e) => setTenant("building", e.target.value)} className="h-12" data-testid="onboard-tenant-building" />
            </Field>
          </FieldRow>
          <div className="flex justify-end pt-2">
            <Button onClick={saveTenant} disabled={busy} className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]" data-testid="onboard-save-tenant">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Save tenant &amp; add vehicle
            </Button>
          </div>
        </div>
      )}

      {step === "vehicle" && (
        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-4" data-testid="onboard-vehicle-step">
          <FieldRow cols={4}>
            <Field label="Year" required>
              <Input value={vehicle.year} onChange={(e) => setVeh("year", e.target.value)} className="h-12" data-testid="onboard-vehicle-year" />
            </Field>
            <Field label="Make" required>
              <Input value={vehicle.make} onChange={(e) => setVeh("make", e.target.value)} className="h-12" data-testid="onboard-vehicle-make" />
            </Field>
            <Field label="Model">
              <Input value={vehicle.model} onChange={(e) => setVeh("model", e.target.value)} className="h-12" data-testid="onboard-vehicle-model" />
            </Field>
            <Field label="Color">
              <Input value={vehicle.color} onChange={(e) => setVeh("color", e.target.value)} className="h-12" data-testid="onboard-vehicle-color" />
            </Field>
          </FieldRow>
          <FieldRow cols={2}>
            <Field label="License plate" required>
              <Input value={vehicle.license_plate} onChange={(e) => setVeh("license_plate", e.target.value)} className="h-12 font-mono uppercase" data-testid="onboard-vehicle-plate" />
            </Field>
            <Field label="State">
              <Select value={vehicle.state} onValueChange={(v) => setVeh("state", v)}>
                <SelectTrigger className="h-12" data-testid="onboard-vehicle-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Permit type (optional)">
              <Select
                value={vehicle.permit_type || "none"}
                onValueChange={(v) => setVeh("permit_type", v === "none" ? "" : v)}
              >
                <SelectTrigger className="h-12" data-testid="onboard-vehicle-permit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {PERMIT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </FieldRow>

          <Field label="Photos (up to 4)">
            <div className="grid grid-cols-4 gap-3">
              {vehicle.images.map((src, i) => (
                <div key={i} className="relative aspect-square border border-[#E4E4E7] rounded-md overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" className="absolute top-1 right-1 bg-black/60 text-white w-6 h-6 rounded-full flex items-center justify-center" onClick={() => setVehicle((v) => ({ ...v, images: v.images.filter((_, j) => j !== i) }))}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {vehicle.images.length < 4 && (
                <button type="button" onClick={() => fileRef.current?.click()} className="aspect-square border-2 border-dashed border-[#E4E4E7] rounded-md flex flex-col items-center justify-center text-[#71717A] text-xs hover:bg-[#F4F4F5]" data-testid="onboard-vehicle-upload">
                  <Upload className="w-5 h-5 mb-1" strokeWidth={1.5} />
                  Upload
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
          </Field>

          <div className="flex flex-wrap gap-2 pt-2">
            {editingVehicleId ? (
              <>
                <Button onClick={() => saveVehicle("finish")} disabled={busy} className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]" data-testid="onboard-save-vehicle-changes">
                  <Save className="w-4 h-4 mr-2" /> Save changes
                </Button>
                <Button
                  onClick={() => {
                    setEditingVehicleId(null);
                    setVehicle(emptyVehicle());
                    if (editTenantId) setStep("edit-tenant");
                    else nav("/dashboard/tenants");
                  }}
                  variant="ghost"
                  className="h-12"
                  data-testid="onboard-cancel-vehicle-edit"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => saveVehicle("another")} disabled={busy} variant="outline" className="h-12" data-testid="onboard-save-add-another">
                  <Save className="w-4 h-4 mr-2" /> Save &amp; add another vehicle
                </Button>
                <Button onClick={() => saveVehicle("finish")} disabled={busy} className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]" data-testid="onboard-save-vehicle">
                  {isInvoiceMode ? (
                    <>
                      <Receipt className="w-4 h-4 mr-2" /> Save &amp; Continue to Invoice
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> Save vehicle
                    </>
                  )}
                </Button>
                <Button onClick={() => saveVehicle("another-tenant")} disabled={busy} variant="ghost" className="h-12" data-testid="onboard-save-add-another-tenant">
                  <UserPlus className="w-4 h-4 mr-2" /> Save &amp; add another tenant
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StepBadge({ num, label, icon: Icon, active, done }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-md px-4 py-3 border-2 ${
        active ? "border-[#0F8F4F] bg-[#0F8F4F]/5" : done ? "border-[#00C16E]/40 bg-[#00C16E]/5" : "border-[#E4E4E7] bg-white"
      }`}
    >
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
          active ? "bg-[#0F8F4F] text-white" : done ? "bg-[#00C16E] text-white" : "bg-[#F4F4F5] text-[#71717A]"
        }`}
      >
        {done ? <Check className="w-3.5 h-3.5" /> : num}
      </div>
      <Icon className={`w-4 h-4 ${active ? "text-[#0F8F4F]" : "text-[#71717A]"}`} strokeWidth={1.5} />
      <span className="font-medium">{label}</span>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2 flex-1">
      <Label className="mono-tiny text-[#71717A]">
        {label} {required && <span className="text-[#EF4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}

function FieldRow({ cols = 1, children }) {
  const grid = { 1: "grid-cols-1", 2: "grid-cols-1 md:grid-cols-2", 4: "grid-cols-2 md:grid-cols-4" }[cols];
  return <div className={`grid ${grid} gap-3`}>{children}</div>;
}
